// src/entities/game/model/useConfigContext.ts
import { ConfigContext } from "@app/app/providers/ConfigContext.tsx";
import { IConfigContext } from "@app/app/providers/ConfigProvider.tsx";
import {
	type FxIdWebFeaturesGamesChooseGamePortalConfig,
	type FxIdWebFeaturesGamesChooseGameResponsePublicGameData
} from "@fxid/openapi-client";
import { useContext } from "react";

/**
 * Базовый хук для доступа к контексту конфигурации.
 * @returns {ConfigContextValue} Значение контекста или null, если провайдер отсутствует.
 */
export const useConfigContext = () => {
	const context = useContext(ConfigContext);
	if (!context) {
		throw new Error("useConfigContext must be used within a ConfigProvider");
	}
	return context;
};

/**
 * @fileoverview "Строгий" хук для получения контекста.
 * Гарантирует, что `portalConfig` существует, иначе выбрасывает ошибку.
 * Следует использовать в компонентах, которые могут рендериться без `gameConfig` (например, на главной портала).
 * @throws {Error} Если `portalConfig` отсутствует.
 * @returns {ConfigContextValue} Контекст с гарантированным `portalConfig`.
 */
export const useRequiredPortalConfigContext = (): IConfigContext => {
	const context = useConfigContext();

	if (!context.portalConfig) {
		throw new Error("useRequiredPortalConfigContext: portalConfig is missing. This indicates a critical error.");
	}

	return context as IConfigContext;
};

export type TConfigContextWithGame = Omit<IConfigContext, "gameConfig"> & {
	gameConfig: FxIdWebFeaturesGamesChooseGameResponsePublicGameData;
};

/**
 * @fileoverview "Строжайший" хук для получения контекста.
 * Гарантирует, что и `portalConfig`, и `gameConfig` существуют, иначе выбрасывает ошибку.
 * Следует использовать в компонентах, которые имеют смысл только в контексте конкретной игры.
 * @throws {Error} Если `portalConfig` или `gameConfig` отсутствует.
 * @returns {GameConfigContextValue} Контекст с гарантированными `portalConfig` и `gameConfig`.
 */
export const useRequiredPortalAndGameConfigContext = (): TConfigContextWithGame => {
	const context = useConfigContext();

	if (!context.portalConfig || !context.gameConfig) {
		log.error("useRequiredPortalAndGameConfigContext: Required configuration is missing.", {
			portal: !!context.portalConfig,
			game: !!context.gameConfig
		});
		throw new Error("Required portal or game-specific configuration is missing.");
	}

	return context as TConfigContextWithGame;
};
